import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { Group, Loader } from '@mantine/core';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import { LoginPage } from './auth/LoginPage';
import { AppLayout } from './layout/AppLayout';
import { DashboardPage } from './pages/DashboardPage';
import { LeadsPage } from './pages/LeadsPage';
import { SegmentsPage } from './pages/SegmentsPage';
import { ReportsPage } from './pages/ReportsPage';
import { RoleListRedirect, RoleRecordRedirect } from './pages/crm/RoleRedirect';
import { AlertsPage } from './pages/AlertsPage';
import { CampaignsPage } from './pages/CampaignsPage';
import { CampaignDetailPage } from './pages/CampaignDetailPage';
import { DestinationsPage } from './pages/DestinationsPage';
import { DestinationDetailPage } from './pages/DestinationDetailPage';
import { RoutesPage } from './pages/streams/RoutesPage';
import { SheetsPage } from './pages/SheetsPage';
import { SheetViewPage } from './pages/SheetViewPage';
import { LogsPage } from './pages/LogsPage';
import { ReturnsPage } from './pages/returns/ReturnsPage';
import { StatementsPage } from './pages/statements/StatementsPage';
import { StatementViewPage } from './pages/statements/StatementViewPage';
import { SettingsPage } from './pages/SettingsPage';
import { EmailsPage } from './pages/EmailsPage';
import { CompaniesPage } from './pages/crm/CompaniesPage';
import { CompanyPage } from './pages/crm/CompanyPage';
import { PersonPage } from './pages/people/PersonPage';
import { SuggestionsPage } from './pages/people/SuggestionsPage';
import { DealsPage } from './pages/crm/DealsPage';
import { TasksPage } from './pages/crm/TasksPage';
import { WorkflowsPage } from './pages/workflows/WorkflowsPage';
import { WorkflowBuilderPage } from './pages/workflows/WorkflowBuilderPage';
import { WorkflowsRedirect } from './pages/workflows/WorkflowsRedirect';
import { BulkSendPage } from './pages/emails/BulkSendPage';
// The editor brings a whole block editor and email renderer with it, so it
// loads only when a template is opened.
const EmailTemplatePage = lazy(() =>
  import('./pages/EmailTemplatePage').then((m) => ({ default: m.EmailTemplatePage })),
);

/**
 * `/streams` and `/streams/:id`, the app's words for campaigns: the pages
 * live at /campaigns (paths never change with the words), so these land
 * there, keeping any query.
 */
function StreamRedirect() {
  const { id } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/campaigns${id ? `/${id}` : ''}${search}`} replace />;
}

/** /rows is the records page, /leads, keeping any query (a segment, a view). */
function RowsRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/leads${search}`} replace />;
}

function Routed() {
  const { token } = useAuth();
  if (!token) return <LoginPage />;
  return (
    <Routes>
      <Route element={<AppLayout />}>
        <Route index element={<DashboardPage />} />
        <Route path="leads" element={<LeadsPage />} />
        {/* Rows is the records page's name; its address stays /leads, and
            /rows (with any filters) opens it there. */}
        <Route path="rows" element={<RowsRedirect />} />
        <Route path="segments" element={<SegmentsPage />} />
        {/* People are the records page's people view now. The old
            address opens it there; a person's own page and the
            suggestions inbox keep their addresses. */}
        <Route path="people" element={<Navigate to="/leads?view=people" replace />} />
        <Route path="people/suggestions" element={<SuggestionsPage />} />
        <Route path="people/:id" element={<PersonPage />} />
        <Route path="companies" element={<CompaniesPage />} />
        <Route path="companies/:id" element={<CompanyPage />} />
        <Route path="deals" element={<DealsPage />} />
        <Route path="tasks" element={<TasksPage />} />
        <Route path="automations" element={<WorkflowsPage />} />
        <Route path="automations/:id" element={<WorkflowBuilderPage />} />
        {/* Workflows are called Automations now; old links and bookmarks
            land on the same page, with their query kept. */}
        <Route path="workflows" element={<WorkflowsRedirect />} />
        <Route path="workflows/:id" element={<WorkflowsRedirect />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="alerts" element={<AlertsPage />} />
        <Route path="campaigns" element={<CampaignsPage />} />
        <Route path="campaigns/:id" element={<CampaignDetailPage />} />
        <Route path="streams" element={<StreamRedirect />} />
        <Route path="streams/:id" element={<StreamRedirect />} />
        {/* Every route of every stream: the menu item Destinations used to
            be. The destinations list itself stays at /destinations. */}
        <Route path="routes" element={<RoutesPage />} />
        {/* Buyers and suppliers are companies with a role: the old
            addresses open the filtered company list, or the record's
            company. */}
        <Route path="buyers" element={<RoleListRedirect kind="buyers" />} />
        <Route path="buyers/:id" element={<RoleRecordRedirect kind="buyers" />} />
        <Route path="suppliers" element={<RoleListRedirect kind="suppliers" />} />
        <Route path="suppliers/:id" element={<RoleRecordRedirect kind="suppliers" />} />
        <Route path="destinations" element={<DestinationsPage />} />
        <Route path="destinations/:id" element={<DestinationDetailPage />} />
        <Route path="sheets" element={<SheetsPage />} />
        <Route path="sheets/:id" element={<SheetViewPage />} />
        <Route path="logs" element={<LogsPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="returns" element={<ReturnsPage />} />
        <Route path="statements" element={<StatementsPage />} />
        <Route path="statements/:id" element={<StatementViewPage />} />
        <Route path="emails" element={<EmailsPage />} />
        <Route path="emails/bulk/:id" element={<BulkSendPage />} />
        <Route
          path="emails/:id"
          element={
            <Suspense fallback={<Group justify="center" py="xl"><Loader size="sm" /></Group>}>
              <EmailTemplatePage />
            </Suspense>
          }
        />
        {/* Bookmarks and old links keep working, landing where the job now
            lives rather than dumping the user on the dashboard. */}
        <Route path="import" element={<Navigate to="/sheets" replace />} />
        <Route path="verify" element={<Navigate to="/sheets" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

export function App() {
  return (
    <AuthProvider>
      <Routed />
    </AuthProvider>
  );
}
